import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  PlusCircle, 
  Calendar, 
  Settings, 
  Share2, 
  QrCode,
  Clock,
  Users,
  FileEdit
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';

export const ActionCenter: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const username = user?.name?.toLowerCase().replace(/\s+/g, '');
  const personalLink = `${window.location.origin}/${username}`;

  const quickActions = [
    {
      label: 'Create Link',
      description: 'Create a new booking link',
      icon: PlusCircle,
      action: () => navigate('/create-booking-link'),
      color: 'text-green-500',
    },
    {
      label: 'Calendar',
      description: 'View your calendar',
      icon: Calendar,
      action: () => navigate('/calendar'),
      color: 'text-blue-500',
    },
    {
      label: 'Services',
      description: 'Manage your services',
      icon: FileEdit,
      action: () => navigate('/services'),
      color: 'text-purple-500',
    },
    {
      label: 'Availability',
      description: 'Set your availability',
      icon: Clock,
      action: () => navigate('/availability'),
      color: 'text-orange-500',
    },
    {
      label: 'Team',
      description: 'Manage your team',
      icon: Users,
      action: () => navigate('/team'),
      color: 'text-indigo-500',
    },
    {
      label: 'Settings',
      description: 'Configure your account',
      icon: Settings,
      action: () => navigate('/settings'),
      color: 'text-gray-500',
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center justify-center gap-2 hover:bg-muted/50"
                onClick={action.action}
              >
                <action.icon className={`h-6 w-6 ${action.color}`} />
                <span className="text-sm font-medium">{action.label}</span>
                <span className="text-xs text-muted-foreground text-center">
                  {action.description}
                </span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Share Your Link</CardTitle>
          <CardDescription>Make it easy for clients to book with you</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1 w-full">
              <div className="space-y-4">
                <div className="bg-muted rounded-md px-4 py-3 font-mono text-sm w-full truncate">
                  {personalLink}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => {
                    navigator.clipboard.writeText(personalLink);
                  }}>
                    <Share2 className="h-4 w-4" />
                    Copy Link
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/share-link')}>
                    <QrCode className="h-4 w-4" />
                    QR Code
                  </Button>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG value={personalLink} size={128} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 